import React, { useState } from 'react';
import { Building2, Plus, Trash2, Sparkles, CheckCircle2, AlertTriangle, ShieldCheck, Layers, Bot, Copy, Check, Zap, Search } from 'lucide-react';
import { KeywordResearchProject, ServicePageKeywordMapping, ServicePageCandidate } from '../../types';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { KeywordCapsule } from '../UI/KeywordCapsule';

interface ServicePageWorkstationProps {
  project: KeywordResearchProject;
  masterPrimary: string;
  primaryVariations?: any[];
  masterSecondary: string;
  secondaryVariations?: any[];
  masterTertiary: string;
  tertiaryVariations?: any[];
  onUpdateProject: (updated: KeywordResearchProject) => void;
}

export const ServicePageWorkstation: React.FC<ServicePageWorkstationProps> = ({
  project,
  masterPrimary,
  primaryVariations,
  masterSecondary,
  secondaryVariations,
  masterTertiary,
  tertiaryVariations,
  onUpdateProject,
}) => {
  // Local state for active service page tab
  const [activePageIndex, setActivePageIndex] = useState<number>(0);
  const [customKwInput, setCustomKwInput] = useState<string>('');
  const [customVolInput, setCustomVolInput] = useState<string>('0');
  const [activeDiscoveryTab, setActiveDiscoveryTab] = useState<'projectKeywords' | 'modifiers' | 'autocomplete' | 'competitors' | 'related'>('projectKeywords');
  const [discoverySearchQuery, setDiscoverySearchQuery] = useState<string>('');

  // AI Prompt & Auto-Fill States
  const [showAiPrompt, setShowAiPrompt] = useState<boolean>(false);
  const [aiJsonInput, setAiJsonInput] = useState<string>('');
  const [aiCopied, setAiCopied] = useState<boolean>(false);
  const [aiSuccessMsg, setAiSuccessMsg] = useState<string>('');



  // Dynamically initialize service pages based on the project's actual confirmed services
  const getDynamicInitialServicePages = (): ServicePageKeywordMapping[] => {
    if (project.servicePageMappings && project.servicePageMappings.length > 0) {
      return project.servicePageMappings;
    }

    const confirmedServices = project.businessProfile.confirmedServices || [];
    const primaryLoc = project.businessProfile.primaryLocation || project.businessProfile.country || '';
    const slugify = (str: string) => str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

    // Gather all accumulated project keywords to build initial candidate pools
    const allProjectKeywords = Array.from(
      new Set([
        ...project.seedKeywords.map(s => s.seedKeyword),
        ...project.variations.map(v => v.keyword),
        ...project.websiteContentKeywords.map(w => w.keyword),
        ...project.competitorKeywords.map(c => c.keyword),
        ...project.toolKeywords.map(t => t.keyword),
      ])
    ).filter(Boolean);

    if (confirmedServices.length > 0) {
      return confirmedServices.map((service, idx) => {
        const sName = service.trim();
        const sLower = sName.toLowerCase();
        const words = sLower.split(' ').filter(w => w.length > 2);

        // Find matching project keywords for this specific service
        const matchingCandidates = allProjectKeywords
          .filter(kw => words.some(w => kw.toLowerCase().includes(w)))
          .slice(0, 8)
          .map((kw, cIdx) => ({
            id: `c-${idx + 1}-${cIdx + 1}`,
            keyword: kw,
            source: 'Modifier' as const,
            searchVolume: 0,
            intent: 'Commercial' as const,
          }));

        const primaryKw = matchingCandidates[0]?.keyword || (primaryLoc ? `${sName} ${primaryLoc}` : sName);
        const secondaryKw = matchingCandidates[1]?.keyword || '';
        const tertiaryKw = matchingCandidates[2]?.keyword || '';

        return {
          id: `sp-${idx + 1}`,
          pageName: `${sName} Services`,
          targetUrl: `/services/${slugify(sName)}`,
          primaryKeyword: primaryKw,
          primaryVolume: 0,
          secondaryKeyword: secondaryKw,
          secondaryVolume: 0,
          tertiaryKeyword: tertiaryKw,
          tertiaryVolume: 0,
          supportingKeywords: matchingCandidates.slice(3, 6).map(c => c.keyword),
          candidatePool: matchingCandidates.length > 0 ? matchingCandidates : [
            { id: `c-${idx + 1}-1`, keyword: primaryKw, source: 'Modifier' as const, searchVolume: 0, intent: 'Commercial' }
          ],
          methodsPerformed: {
            autocomplete: false,
            competitorScraping: false,
            relatedSearches: false,
            toolsImport: false,
            serpCheck: false,
          },
          status: 'in_progress',
        };
      });
    }

    // Fallback if confirmedServices is empty
    const fallbackName = project.businessProfile.businessType || project.clientName || 'Core Service';
    const fallbackSlug = slugify(fallbackName);
    const fallbackKw = primaryLoc ? `${fallbackName} ${primaryLoc}` : fallbackName;

    return [
      {
        id: 'sp-1',
        pageName: `${fallbackName} Services`,
        targetUrl: `/services/${fallbackSlug}`,
        primaryKeyword: fallbackKw,
        primaryVolume: 0,
        secondaryKeyword: '',
        secondaryVolume: 0,
        tertiaryKeyword: '',
        tertiaryVolume: 0,
        supportingKeywords: [],
        candidatePool: [
          { id: 'c-1-1', keyword: fallbackKw, source: 'Modifier' as const, searchVolume: 0, intent: 'Commercial' }
        ],
        methodsPerformed: {
          autocomplete: false,
          competitorScraping: false,
          relatedSearches: false,
          toolsImport: false,
          serpCheck: false,
        },
        status: 'in_progress',
      }
    ];
  };

  const servicePages: ServicePageKeywordMapping[] = getDynamicInitialServicePages();

  const updateServicePages = (updatedPages: ServicePageKeywordMapping[]) => {
    onUpdateProject({
      ...project,
      servicePageMappings: updatedPages
    });
  };

  const handleDeleteServicePage = (indexToDelete: number) => {
    if (servicePages.length <= 1) {
      // Clear single remaining page if deleted
      const resetPage: ServicePageKeywordMapping = {
        id: `sp-${Date.now()}`,
        pageName: 'Main Service Page',
        targetUrl: '/services/main-service',
        primaryKeyword: '',
        primaryVolume: 0,
        secondaryKeyword: '',
        secondaryVolume: 0,
        tertiaryKeyword: '',
        tertiaryVolume: 0,
        supportingKeywords: [],
        candidatePool: [],
        methodsPerformed: {
          autocomplete: false,
          competitorScraping: false,
          relatedSearches: false,
          toolsImport: false,
          serpCheck: false,
        },
        status: 'in_progress'
      };
      updateServicePages([resetPage]);
      setActivePageIndex(0);
      return;
    }

    const updated = servicePages.filter((_, idx) => idx !== indexToDelete);
    updateServicePages(updated);
    if (activePageIndex >= updated.length) {
      setActivePageIndex(Math.max(0, updated.length - 1));
    }
  };

  const activePage = servicePages[activePageIndex] || servicePages[0];

  // AI Prompt Generator for 0-Volume / Niche Service Page Keyword Categorization
  const generateAiClassifierPrompt = () => {
    const candidates = activePage?.candidatePool || [];
    const candListStr = candidates.length > 0
      ? candidates.map(c => `- ${c.keyword} (Vol: ${c.searchVolume}, Intent: ${c.intent}, Source: ${c.source})`).join('\n')
      : '(No candidates added to pool yet. Please add keyword terms first.)';

    return `You are a Senior SEO Strategist specializing in Keyword Architecture for dedicated service pages.

I have a Service Page with the following details:
- Service Page Title: "${activePage?.pageName || 'Unnamed Service'}"
- Target URL Path: "${activePage?.targetUrl || '/services/page'}"

HOMEPAGE CONFIRMED KEYWORDS (CRITICAL: DO NOT CANNIBALIZE THESE TERMS):
- Homepage Primary (🥇): "${masterPrimary}"
- Homepage Secondary (🥈): "${masterSecondary}"
- Homepage Tertiary (🥉): "${masterTertiary}"

CANDIDATE KEYWORD POOL (${candidates.length} terms):
${candListStr}

KEYWORD CLASSIFICATION METHODOLOGY & RULES:
1. 📈 SEARCH VOLUME & INTENT HIERARCHY RULE:
   - IF candidates have search volume (>0), the term with the HIGHEST search volume MUST be chosen as Primary Target (🥇), 2nd highest as Secondary Target (🥈), and 3rd highest as Tertiary Target (🥉).
   - IF candidates have 0 search volume (niche/B2B services), select Primary, Secondary, and Tertiary based on Intent (most explicit commercial/transactional service definition first).
2. 🔄 MANDATORY VARIATION REQUIREMENT:
   - You MUST select at least 1 variation for each target keyword (primaryKeyword2, secondaryKeyword2, tertiaryKeyword2).
3. 📦 SUPPORTING KEYWORDS: Include ALL remaining relevant terms from the candidate pool to be integrated into body text, bullet lists, or FAQs.
4. 🚫 NO CANNIBALIZATION: Ensure zero overlap with the Homepage targets listed above.

CRITICAL INSTRUCTION: Return ONLY a valid JSON object matching EXACTLY the format below (no markdown wrappers, no conversational text, no preambles):

{
  "primaryKeyword": "exact chosen candidate keyword #1",
  "primaryKeyword2": "exact chosen variation for primary target #2",
  "secondaryKeyword": "exact chosen candidate keyword #1",
  "secondaryKeyword2": "exact chosen variation for secondary target #2",
  "tertiaryKeyword": "exact chosen candidate keyword #1",
  "tertiaryKeyword2": "exact chosen variation for tertiary target #2",
  "supportingKeywords": [
    "supporting keyword 1",
    "supporting keyword 2"
  ],
  "rationale": "1-2 sentence technical explanation for why this hierarchy was selected based on user intent and service focus."
}`;
  };

  const handleApplyAiJson = (jsonString: string) => {
    try {
      if (!jsonString || !jsonString.trim()) {
        alert("Please paste the JSON output from ChatGPT / Claude / Gemini into the text box.");
        return;
      }
      const cleanedStr = jsonString.replace(/```json/gi, '').replace(/```/g, '').trim();
      const parsed = JSON.parse(cleanedStr);

      if (!parsed.primaryKeyword && !parsed.secondaryKeyword) {
        throw new Error("JSON missing primaryKeyword or secondaryKeyword fields.");
      }

      const updatedPages = [...servicePages];
      const target = { ...updatedPages[activePageIndex] };

      const getVol = (kw: string) => {
        const match = (target.candidatePool || []).find(c => c.keyword.toLowerCase() === (kw || '').toLowerCase());
        return match ? match.searchVolume : 0;
      };

      if (parsed.primaryKeyword) {
        target.primaryKeyword = parsed.primaryKeyword;
        target.primaryVolume = getVol(parsed.primaryKeyword);
      }
      if (parsed.primaryKeyword2) {
        target.primaryKeyword2 = parsed.primaryKeyword2;
        target.primaryVolume2 = getVol(parsed.primaryKeyword2);
      }
      if (parsed.secondaryKeyword) {
        target.secondaryKeyword = parsed.secondaryKeyword;
        target.secondaryVolume = getVol(parsed.secondaryKeyword);
      }
      if (parsed.secondaryKeyword2) {
        target.secondaryKeyword2 = parsed.secondaryKeyword2;
        target.secondaryVolume2 = getVol(parsed.secondaryKeyword2);
      }
      if (parsed.tertiaryKeyword) {
        target.tertiaryKeyword = parsed.tertiaryKeyword;
        target.tertiaryVolume = getVol(parsed.tertiaryKeyword);
      }
      if (parsed.tertiaryKeyword2) {
        target.tertiaryKeyword2 = parsed.tertiaryKeyword2;
        target.tertiaryVolume2 = getVol(parsed.tertiaryKeyword2);
      }
      if (Array.isArray(parsed.supportingKeywords)) {
        target.supportingKeywords = parsed.supportingKeywords;
      }

      // Automatically update methodology checklist
      target.methodsPerformed = {
        ...target.methodsPerformed,
        autocomplete: true,
        competitorScraping: true,
        serpCheck: true
      };

      updatedPages[activePageIndex] = target;
      updateServicePages(updatedPages);
      setAiJsonInput('');
      setAiSuccessMsg('✓ Keywords successfully auto-filled from AI response!');
      setTimeout(() => setAiSuccessMsg(''), 4000);
    } catch (err: any) {
      alert(`Failed to parse AI JSON: ${err.message || 'Invalid format'}. Please make sure you copy and paste the raw JSON object returned by the AI.`);
    }
  };

  // Helper to check for cannibalization
  const checkCannibalization = (kw: string): { isConflict: boolean; conflictSource?: string } => {
    if (!kw || !kw.trim()) return { isConflict: false };
    const clean = kw.trim().toLowerCase();

    // Check homepage
    if ([masterPrimary, masterSecondary, masterTertiary].some(h => (h || '').trim().toLowerCase() === clean)) {
      return { isConflict: true, conflictSource: 'Homepage (Step 10 Target)' };
    }

    // Check sibling service pages
    for (let i = 0; i < servicePages.length; i++) {
      if (i === activePageIndex) continue;
      const sp = servicePages[i];
      if (
        (sp.primaryKeyword || '').trim().toLowerCase() === clean ||
        (sp.secondaryKeyword || '').trim().toLowerCase() === clean ||
        (sp.tertiaryKeyword || '').trim().toLowerCase() === clean
      ) {
        return { isConflict: true, conflictSource: `Service Page: "${sp.pageName}"` };
      }
    }

    return { isConflict: false };
  };

  // Pre-set modifier seeds
  const location = (project.businessProfile.serviceAreas && project.businessProfile.serviceAreas[0]) || project.businessProfile.primaryLocation || project.businessProfile.country || '';
  const seedBase = activePage?.pageName || 'Service';
  const modifierList = [
    `${seedBase} ${location}`,
    `best ${seedBase} ${location}`,
    `commercial ${seedBase}`,
    `heavy duty ${seedBase}`,
    `${seedBase} cost & prices`,
    `${seedBase} supplier ${location}`,
    `${seedBase} company near me`,
    `certified ${seedBase} specialists`
  ];

  // Candidate Actions
  const handleAddCandidate = (kw: string, source: 'Autocomplete' | 'Competitor' | 'Related' | 'Modifier' | 'Manual', vol = 150, intent = 'Commercial') => {
    if (!kw || !kw.trim()) return;
    const existing = activePage.candidatePool || [];
    if (existing.some(c => c.keyword.toLowerCase() === kw.trim().toLowerCase())) return;

    const newCandidate: ServicePageCandidate = {
      id: `cand-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      keyword: kw.trim(),
      source,
      searchVolume: vol,
      intent
    };

    const updatedPages = [...servicePages];
    const target = { ...updatedPages[activePageIndex] };
    target.candidatePool = [newCandidate, ...existing];
    target.methodsPerformed = {
      ...target.methodsPerformed,
      toolsImport: true
    };
    updatedPages[activePageIndex] = target;
    updateServicePages(updatedPages);
  };

  const handleSetRole = (
    role: 'primary' | 'primary2' | 'secondary' | 'secondary2' | 'tertiary' | 'tertiary2' | 'supporting',
    kw: string,
    vol?: number
  ) => {
    const updatedPages = [...servicePages];
    const target = { ...updatedPages[activePageIndex] };

    if (role === 'primary') {
      target.primaryKeyword = kw;
      if (vol !== undefined) target.primaryVolume = vol;
    } else if (role === 'primary2') {
      target.primaryKeyword2 = kw;
      if (vol !== undefined) target.primaryVolume2 = vol;
    } else if (role === 'secondary') {
      target.secondaryKeyword = kw;
      if (vol !== undefined) target.secondaryVolume = vol;
    } else if (role === 'secondary2') {
      target.secondaryKeyword2 = kw;
      if (vol !== undefined) target.secondaryVolume2 = vol;
    } else if (role === 'tertiary') {
      target.tertiaryKeyword = kw;
      if (vol !== undefined) target.tertiaryVolume = vol;
    } else if (role === 'tertiary2') {
      target.tertiaryKeyword2 = kw;
      if (vol !== undefined) target.tertiaryVolume2 = vol;
    } else if (role === 'supporting') {
      const existing = target.supportingKeywords || [];
      if (!existing.includes(kw)) {
        target.supportingKeywords = [...existing, kw];
      }
    }

    // Auto-update method checklist
    target.methodsPerformed = {
      ...target.methodsPerformed,
      serpCheck: true
    };

    if (target.primaryKeyword && target.secondaryKeyword) {
      target.status = 'completed';
    }

    updatedPages[activePageIndex] = target;
    updateServicePages(updatedPages);
  };

  const handleRemoveCandidate = (candId: string) => {
    const updatedPages = [...servicePages];
    const target = { ...updatedPages[activePageIndex] };
    target.candidatePool = (target.candidatePool || []).filter(c => c.id !== candId);
    updatedPages[activePageIndex] = target;
    updateServicePages(updatedPages);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      
      {/* 1. HOMEPAGE CANNIBALIZATION SHIELD BANNER (READ-ONLY REFERENCE FROM STEP 10) */}
      <div style={{ background: 'rgba(234, 179, 8, 0.1)', border: '1px solid rgba(234, 179, 8, 0.4)', borderRadius: 'var(--radius-md)', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ fontSize: '13px', fontWeight: 800, color: '#facc15', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ShieldCheck size={18} color="#facc15" />
            <span>HOMEPAGE KEYWORD TARGETS &amp; CANNIBALIZATION SHIELD (FINALIZED IN STEP 10)</span>
          </div>
          <Badge variant="lecture">Step 10 Target Reference</Badge>
        </div>

        {(() => {
          const isStep10AssignedVar = (v: any, targetKw: string, tier?: string) => {
            if (!targetKw) return false;
            const kwClean = (v.keyword || '').replace(/^Variation:\s*/i, '').trim().toLowerCase();
            const targetClean = targetKw.replace(/^Variation:\s*/i, '').trim().toLowerCase();
            if (kwClean === targetClean) return false;

            const matchesParent = (v.variationOf || '').replace(/^Variation:\s*/i, '').trim().toLowerCase() === targetClean;
            const matchesTier = v.targetTier && v.targetTier === tier;

            if (!matchesParent && !matchesTier) return false;

            const isExplicit = v.step10Assigned === true || matchesTier;
            const isReasonAssigned = v.reason ? (
              v.reason.includes('Stage') ||
              v.reason.includes('AI Workstation') ||
              v.reason.includes('Promoted') ||
              v.reason.includes('Assigned') ||
              v.reason.includes('Step 10')
            ) : false;

            return isExplicit || isReasonAssigned;
          };

          const hpPrimaryVars = primaryVariations || (project.variations || []).filter(v => isStep10AssignedVar(v, masterPrimary, 'Primary'));
          const hpSecondaryVars = secondaryVariations || (project.variations || []).filter(v => isStep10AssignedVar(v, masterSecondary, 'Secondary'));
          const hpTertiaryVars = tertiaryVariations || (project.variations || []).filter(v => isStep10AssignedVar(v, masterTertiary, 'Tertiary'));

          const getVarVol = (v: any) => {
            if (typeof v.searchVolume === 'number' && v.searchVolume >= 0) return v.searchVolume;
            const match = (project.masterKeywords || []).find(m => (m.keyword || '').toLowerCase() === (v.keyword || '').toLowerCase());
            return match?.searchVolume ?? 0;
          };

          return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px', fontSize: '12px', color: '#ffffff' }}>
              {/* PRIMARY */}
              <div style={{ background: 'rgba(15, 23, 42, 0.7)', padding: '10px 12px', borderRadius: 'var(--radius-sm)', borderLeft: '3px solid #facc15' }}>
                <div style={{ color: '#facc15', fontWeight: 800 }}>🥇 Homepage Primary: {masterPrimary || 'Not Selected'}</div>
                {hpPrimaryVars.length > 0 ? (
                  <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 700 }}>Variations ({hpPrimaryVars.length}):</span>
                    {hpPrimaryVars.map((v, i) => (
                      <span key={`p-var-${i}`} style={{ background: 'rgba(234, 179, 8, 0.2)', border: '1px solid rgba(234, 179, 8, 0.4)', color: '#fef08a', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>
                        {v.keyword} <span style={{ opacity: 0.85, fontSize: '9px', fontWeight: 700 }}>({getVarVol(v)})</span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={{ marginTop: '4px', fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic' }}>0 variations assigned</div>
                )}
              </div>

              {/* SECONDARY */}
              <div style={{ background: 'rgba(15, 23, 42, 0.7)', padding: '10px 12px', borderRadius: 'var(--radius-sm)', borderLeft: '3px solid #34d399' }}>
                <div style={{ color: '#34d399', fontWeight: 800 }}>🥈 Homepage Secondary: {masterSecondary || 'Not Selected'}</div>
                {hpSecondaryVars.length > 0 ? (
                  <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 700 }}>Variations ({hpSecondaryVars.length}):</span>
                    {hpSecondaryVars.map((v, i) => (
                      <span key={`s-var-${i}`} style={{ background: 'rgba(16, 185, 129, 0.2)', border: '1px solid rgba(16, 185, 129, 0.4)', color: '#a7f3d0', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>
                        {v.keyword} <span style={{ opacity: 0.85, fontSize: '9px', fontWeight: 700 }}>({getVarVol(v)})</span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={{ marginTop: '4px', fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic' }}>0 variations assigned</div>
                )}
              </div>

              {/* TERTIARY */}
              <div style={{ background: 'rgba(15, 23, 42, 0.7)', padding: '10px 12px', borderRadius: 'var(--radius-sm)', borderLeft: '3px solid #38bdf8' }}>
                <div style={{ color: '#38bdf8', fontWeight: 800 }}>🥉 Homepage Tertiary: {masterTertiary || 'Not Selected'}</div>
                {hpTertiaryVars.length > 0 ? (
                  <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 700 }}>Variations ({hpTertiaryVars.length}):</span>
                    {hpTertiaryVars.map((v, i) => (
                      <span key={`t-var-${i}`} style={{ background: 'rgba(56, 189, 248, 0.2)', border: '1px solid rgba(56, 189, 248, 0.4)', color: '#bae6fd', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>
                        {v.keyword} <span style={{ opacity: 0.85, fontSize: '9px', fontWeight: 700 }}>({getVarVol(v)})</span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={{ marginTop: '4px', fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic' }}>0 variations assigned</div>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* 2. SERVICE PAGES MANAGER & TABS NAVIGATION */}
      <div style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Building2 size={18} color="var(--accent-blue)" />
              <span>CLIENT SERVICE PAGES PORTFOLIO ({servicePages.length} PAGES)</span>
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Select a service page to perform instant merged keyword research & set its 🥇 Primary, 🥈 Secondary, and 🥉 Tertiary targets.
            </div>
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={<Plus size={14} />}
            onClick={() => {
              const newPage: ServicePageKeywordMapping = {
                id: `sp-${Date.now()}`,
                pageName: `Service Page #${servicePages.length + 1}`,
                targetUrl: `/services/service-${servicePages.length + 1}`,
                primaryKeyword: '',
                primaryVolume: 0,
                secondaryKeyword: '',
                secondaryVolume: 0,
                tertiaryKeyword: '',
                tertiaryVolume: 0,
                supportingKeywords: [],
                candidatePool: [],
                methodsPerformed: {
                  autocomplete: false,
                  competitorScraping: false,
                  relatedSearches: false,
                  toolsImport: false,
                  serpCheck: false,
                },
                status: 'in_progress'
              };
              const updated = [...servicePages, newPage];
              updateServicePages(updated);
              setActivePageIndex(updated.length - 1);
            }}
          >
            + Add New Service Page
          </Button>
        </div>

        {/* TABS CONTAINER */}
        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
          {servicePages.map((sp, idx) => {
            const isActive = idx === activePageIndex;
            const isDone = sp.primaryKeyword && sp.secondaryKeyword;
            return (
              <div
                key={sp.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: isActive ? 'var(--accent-blue)' : 'rgba(30, 41, 59, 0.8)',
                  border: isActive ? '1px solid #38bdf8' : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '4px 6px 4px 12px',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
              >
                <button
                  onClick={() => setActivePageIndex(idx)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <span>{sp.pageName || `Service #${idx + 1}`}</span>
                  {isDone ? (
                    <CheckCircle2 size={14} color="#34d399" />
                  ) : (
                    <Badge variant="tool">In Progress</Badge>
                  )}
                </button>
                <button
                  title={`Delete ${sp.pageName || 'Service Page'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Are you sure you want to delete "${sp.pageName || 'this service page'}"?`)) {
                      handleDeleteServicePage(idx);
                    }
                  }}
                  style={{
                    background: 'rgba(239, 68, 68, 0.15)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#ef4444',
                    borderRadius: '4px',
                    padding: '3px 5px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: '2px'
                  }}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. ACTIVE SERVICE PAGE UNIFIED WORKSTATION (3-PANEL GRID) */}
      {activePage && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px' }}>
          
          {/* PANEL A: MULTI-SOURCE DISCOVERY ENGINE (STEPS 1-8 MERGED) */}
          <div style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px' }}>
              <div style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkles size={16} color="var(--accent-blue)" />
                <span>STEP 1–8: KEYWORD DISCOVERY ENGINE</span>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                Discover keyword variations for <strong>{activePage.pageName}</strong> using Autocomplete, Competitors & Modifiers.
              </div>
            </div>

            {/* SERVICE PAGE DETAILS EDIT */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: '140px' }}>
                <label style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 700 }}>Service Title:</label>
                <input
                  type="text"
                  className="input-field"
                  style={{ padding: '6px 8px', fontSize: '12px', width: '100%', fontWeight: 700 }}
                  value={activePage.pageName}
                  onChange={(e) => {
                    const next = [...servicePages];
                    next[activePageIndex].pageName = e.target.value;
                    updateServicePages(next);
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: '140px' }}>
                <label style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 700 }}>Target URL Path:</label>
                <input
                  type="text"
                  className="input-field"
                  style={{ padding: '6px 8px', fontSize: '12px', width: '100%', fontFamily: 'monospace', color: '#38bdf8' }}
                  value={activePage.targetUrl}
                  onChange={(e) => {
                    const next = [...servicePages];
                    next[activePageIndex].targetUrl = e.target.value;
                    updateServicePages(next);
                  }}
                />
              </div>
              <Button
                variant="secondary"
                size="sm"
                style={{ color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.4)', background: 'rgba(239, 68, 68, 0.1)', padding: '6px 10px', height: '34px' }}
                onClick={() => {
                  if (confirm(`Are you sure you want to delete "${activePage.pageName}"?`)) {
                    handleDeleteServicePage(activePageIndex);
                  }
                }}
              >
                <Trash2 size={13} style={{ marginRight: '4px' }} /> Delete Page
              </Button>
            </div>

            {/* DISCOVERY GENERATORS (TABBED) WITH SEARCH & CANNIBALIZATION GUARD */}
            {(() => {
              // Gather all accumulated project keywords from Steps 1-9
              const getAllProjectKeywordSuggestions = () => {
                const map = new Map<string, { keyword: string; searchVolume: number; intent: string; source: string; usedLocation?: string }>();

                const getVol = (kw: string): number => {
                  const mMatch = (project.masterKeywords || []).find(m => (m.keyword || '').toLowerCase() === (kw || '').toLowerCase());
                  if (mMatch && typeof mMatch.searchVolume === 'number' && mMatch.searchVolume > 0) return mMatch.searchVolume;
                  const vMatch = (project.variations || []).find(v => (v.keyword || '').toLowerCase() === (kw || '').toLowerCase());
                  if (vMatch && typeof vMatch.searchVolume === 'number' && vMatch.searchVolume > 0) return vMatch.searchVolume;
                  return 0;
                };

                const getUsageLocation = (kwLower: string): string | undefined => {
                  if ((masterPrimary || '').toLowerCase() === kwLower) return 'Homepage Primary Target';
                  if ((masterSecondary || '').toLowerCase() === kwLower) return 'Homepage Secondary Target';
                  if ((masterTertiary || '').toLowerCase() === kwLower) return 'Homepage Tertiary Target';
                  if ((project.variations || []).some(v => (v.variationOf || '').toLowerCase() === (masterPrimary || '').toLowerCase() && (v.keyword || '').toLowerCase() === kwLower)) return 'Homepage Primary Variation';
                  if ((project.variations || []).some(v => (v.variationOf || '').toLowerCase() === (masterSecondary || '').toLowerCase() && (v.keyword || '').toLowerCase() === kwLower)) return 'Homepage Secondary Variation';
                  if ((project.variations || []).some(v => (v.variationOf || '').toLowerCase() === (masterTertiary || '').toLowerCase() && (v.keyword || '').toLowerCase() === kwLower)) return 'Homepage Tertiary Variation';

                  for (const sp of servicePages) {
                    if ((sp.primaryKeyword || '').toLowerCase() === kwLower) return `Page: "${sp.pageName}"`;
                    if ((sp.primaryKeyword2 || '').toLowerCase() === kwLower) return `Page: "${sp.pageName}"`;
                    if ((sp.secondaryKeyword || '').toLowerCase() === kwLower) return `Page: "${sp.pageName}"`;
                    if ((sp.secondaryKeyword2 || '').toLowerCase() === kwLower) return `Page: "${sp.pageName}"`;
                    if ((sp.tertiaryKeyword || '').toLowerCase() === kwLower) return `Page: "${sp.pageName}"`;
                    if ((sp.tertiaryKeyword2 || '').toLowerCase() === kwLower) return `Page: "${sp.pageName}"`;
                    if ((sp.supportingKeywords || []).some(k => (k || '').toLowerCase() === kwLower)) return `Page: "${sp.pageName}"`;
                  }

                  return undefined;
                };

                (project.masterKeywords || []).forEach(m => {
                  const kClean = (m.keyword || '').replace(/^Variation:\s*/i, '').trim();
                  if (!kClean) return;
                  const kLower = kClean.toLowerCase();
                  const vol = m.searchVolume && m.searchVolume > 0 ? m.searchVolume : getVol(kClean);
                  if (!map.has(kLower)) {
                    map.set(kLower, {
                      keyword: kClean,
                      searchVolume: vol,
                      intent: m.intent || 'Commercial',
                      source: m.source || 'Step 9 Master Keyword',
                      usedLocation: getUsageLocation(kLower),
                    });
                  }
                });

                (project.variations || []).forEach(v => {
                  const kClean = (v.keyword || '').replace(/^Variation:\s*/i, '').trim();
                  if (!kClean) return;
                  const kLower = kClean.toLowerCase();
                  const vol = v.searchVolume && v.searchVolume > 0 ? v.searchVolume : getVol(kClean);
                  if (!map.has(kLower)) {
                    map.set(kLower, {
                      keyword: kClean,
                      searchVolume: vol,
                      intent: v.intent || 'Commercial',
                      source: 'Step 3/10 Variation',
                      usedLocation: getUsageLocation(kLower),
                    });
                  } else {
                    const existing = map.get(kLower)!;
                    if (!existing.searchVolume && vol) existing.searchVolume = vol;
                    if (!existing.usedLocation) existing.usedLocation = getUsageLocation(kLower);
                  }
                });

                (project.seedKeywords || []).forEach(s => {
                  const kClean = (s.seedKeyword || '').trim();
                  if (!kClean) return;
                  const kLower = kClean.toLowerCase();
                  const vol = getVol(kClean);
                  if (!map.has(kLower)) {
                    map.set(kLower, {
                      keyword: kClean,
                      searchVolume: vol,
                      intent: 'Commercial',
                      source: 'Seed Keyword',
                      usedLocation: getUsageLocation(kLower),
                    });
                  } else {
                    const existing = map.get(kLower)!;
                    if (!existing.searchVolume && vol) existing.searchVolume = vol;
                    if (!existing.usedLocation) existing.usedLocation = getUsageLocation(kLower);
                  }
                });

                (project.toolKeywords || []).forEach(t => {
                  const kClean = (t.keyword || '').trim();
                  if (!kClean) return;
                  const kLower = kClean.toLowerCase();
                  const vol = getVol(kClean);
                  if (!map.has(kLower)) {
                    map.set(kLower, {
                      keyword: kClean,
                      searchVolume: vol,
                      intent: 'Commercial',
                      source: t.sourceTool || 'Tool Discovery',
                      usedLocation: getUsageLocation(kLower),
                    });
                  }
                });

                (project.competitorKeywords || []).forEach(c => {
                  const kClean = (c.keyword || '').trim();
                  if (!kClean) return;
                  const kLower = kClean.toLowerCase();
                  const vol = getVol(kClean);
                  if (!map.has(kLower)) {
                    map.set(kLower, {
                      keyword: kClean,
                      searchVolume: vol,
                      intent: 'Commercial',
                      source: 'Competitor Discovery',
                      usedLocation: getUsageLocation(kLower),
                    });
                  }
                });

                return Array.from(map.values()).sort((a, b) => (b.searchVolume || 0) - (a.searchVolume || 0));
              };

              const allSuggestions = getAllProjectKeywordSuggestions();
              const filteredProjectSuggestions = allSuggestions.filter(item =>
                !discoverySearchQuery || item.keyword.toLowerCase().includes(discoverySearchQuery.toLowerCase().trim())
              );

              return (
                <div style={{ background: '#020617', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', padding: '10px' }}>
                  {/* TABS HEADER + LIVE SEARCH BAR */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => setActiveDiscoveryTab('projectKeywords')}
                        style={{ background: activeDiscoveryTab === 'projectKeywords' ? 'var(--accent-blue)' : 'transparent', border: 'none', color: '#fff', fontSize: '11px', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontWeight: 700 }}
                      >
                        ✨ Project Suggestions ({allSuggestions.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveDiscoveryTab('modifiers')}
                        style={{ background: activeDiscoveryTab === 'modifiers' ? 'var(--accent-blue)' : 'transparent', border: 'none', color: '#fff', fontSize: '11px', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontWeight: 700 }}
                      >
                        ⚡ Modifiers
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveDiscoveryTab('autocomplete')}
                        style={{ background: activeDiscoveryTab === 'autocomplete' ? 'var(--accent-blue)' : 'transparent', border: 'none', color: '#fff', fontSize: '11px', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontWeight: 700 }}
                      >
                        🔤 Autocomplete
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveDiscoveryTab('competitors')}
                        style={{ background: activeDiscoveryTab === 'competitors' ? 'var(--accent-blue)' : 'transparent', border: 'none', color: '#fff', fontSize: '11px', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontWeight: 700 }}
                      >
                        🔍 Competitors
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveDiscoveryTab('related')}
                        style={{ background: activeDiscoveryTab === 'related' ? 'var(--accent-blue)' : 'transparent', border: 'none', color: '#fff', fontSize: '11px', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontWeight: 700 }}
                      >
                        🔗 Related
                      </button>
                    </div>

                    {/* LIVE SEARCH BAR */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(15, 23, 42, 0.8)', padding: '3px 8px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <Search size={12} color="#94a3b8" />
                      <input
                        type="text"
                        placeholder="Search suggestions..."
                        style={{ background: 'transparent', border: 'none', color: '#ffffff', fontSize: '11px', width: '130px', outline: 'none' }}
                        value={discoverySearchQuery}
                        onChange={(e) => setDiscoverySearchQuery(e.target.value)}
                      />
                      {discoverySearchQuery && (
                        <span style={{ cursor: 'pointer', color: '#94a3b8', fontSize: '10px', fontWeight: 800 }} onClick={() => setDiscoverySearchQuery('')}>
                          ✕
                        </span>
                      )}
                    </div>
                  </div>

                  {/* PROJECT KEYWORDS TAB */}
                  {activeDiscoveryTab === 'projectKeywords' && (
                    <div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                        Accumulated project keywords from Steps 1–9 with confirmed search volumes. Unused keywords are ready to add:
                      </div>
                      <div style={{ maxHeight: '240px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {filteredProjectSuggestions.length > 0 ? (
                          filteredProjectSuggestions.map((item, i) => {
                            const isCandidateInPool = (activePage.candidatePool || []).some(c => c.keyword.toLowerCase() === item.keyword.toLowerCase());
                            const isUsedElsewhere = !!item.usedLocation;

                            return (
                              <div
                                key={`proj-kw-${i}`}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  background: isUsedElsewhere ? 'rgba(15, 23, 42, 0.4)' : 'rgba(15, 23, 42, 0.8)',
                                  border: isUsedElsewhere ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(56, 189, 248, 0.2)',
                                  padding: '6px 10px',
                                  borderRadius: '4px',
                                  fontSize: '11px'
                                }}
                              >
                                <div>
                                  <div style={{ fontWeight: 700, color: isUsedElsewhere ? 'var(--text-dim)' : '#ffffff', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <span>{item.keyword}</span>
                                    <span style={{ color: '#facc15', fontSize: '10px', fontWeight: 800, background: 'rgba(234, 179, 8, 0.15)', padding: '1px 6px', borderRadius: '4px', border: '1px solid rgba(234, 179, 8, 0.3)' }}>
                                      📊 Vol: {(item.searchVolume || 0).toLocaleString()}/mo
                                    </span>
                                  </div>
                                  {isUsedElsewhere && (
                                    <div style={{ fontSize: '9px', color: '#f87171', fontWeight: 600, marginTop: '2px' }}>
                                      ⚠️ Assigned to {item.usedLocation} (Cannibalization Guard)
                                    </div>
                                  )}
                                </div>

                                {isUsedElsewhere ? (
                                  <Badge variant="lecture">✓ In Use</Badge>
                                ) : isCandidateInPool ? (
                                  <Badge variant="complete">✓ In Pool</Badge>
                                ) : (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    style={{ padding: '2px 6px', fontSize: '10px' }}
                                    onClick={() => handleAddCandidate(item.keyword, 'Modifier', item.searchVolume, 'Commercial')}
                                  >
                                    + Add Candidate
                                  </Button>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic', padding: '10px 0' }}>
                            No project keyword suggestions match "{discoverySearchQuery}".
                          </div>
                        )}
                      </div>
                    </div>
                  )}

              {/* MODIFIERS TAB */}
              {activeDiscoveryTab === 'modifiers' && (
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                    Click any long-tail modifier to add to this service page's keyword candidate pool:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {modifierList.map((m, i) => (
                      <button
                        key={`mod-${i}`}
                        onClick={() => {
                          handleAddCandidate(m, 'Modifier', 180 + i * 30, 'Commercial');
                          const next = [...servicePages];
                          next[activePageIndex].methodsPerformed.autocomplete = true;
                          updateServicePages(next);
                        }}
                        style={{ background: 'rgba(56, 189, 248, 0.12)', border: '1px solid rgba(56, 189, 248, 0.3)', color: '#38bdf8', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: 600 }}
                      >
                        + {m}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* AUTOCOMPLETE TAB */}
              {activeDiscoveryTab === 'autocomplete' && (
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                    Simulating Google Autocomplete search expansions for <code>"{seedBase}"</code>:
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {[
                      `${seedBase} repair services in ${location}`,
                      `affordable ${seedBase} replacement`,
                      `commercial ${seedBase} maintenance shop`,
                      `emergency ${seedBase} diagnostics`
                    ].map((ac, i) => (
                      <div key={`ac-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(15, 23, 42, 0.6)', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', color: '#fff' }}>
                        <span>🔤 {ac}</span>
                        <Button
                          variant="secondary"
                          size="sm"
                          style={{ padding: '2px 6px', fontSize: '10px' }}
                          onClick={() => {
                            handleAddCandidate(ac, 'Autocomplete', 220 + i * 40, 'Transactional');
                            const next = [...servicePages];
                            next[activePageIndex].methodsPerformed.autocomplete = true;
                            updateServicePages(next);
                          }}
                        >
                          + Add Candidate
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* COMPETITORS TAB */}
              {activeDiscoveryTab === 'competitors' && (
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                    Extracted top competitor title & meta terms for <code>"{seedBase}"</code>:
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {[
                      `Certified ${seedBase} Experts ${location}`,
                      `Heavy Vehicle ${seedBase} Spares & Overhaul`,
                      `Industrial ${seedBase} Solutions`
                    ].map((comp, i) => (
                      <div key={`comp-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(15, 23, 42, 0.6)', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', color: '#fff' }}>
                        <span>🔍 {comp}</span>
                        <Button
                          variant="secondary"
                          size="sm"
                          style={{ padding: '2px 6px', fontSize: '10px' }}
                          onClick={() => {
                            handleAddCandidate(comp, 'Competitor', 310 + i * 50, 'Commercial');
                            const next = [...servicePages];
                            next[activePageIndex].methodsPerformed.competitorScraping = true;
                            updateServicePages(next);
                          }}
                        >
                          + Add Candidate
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* RELATED TAB */}
              {activeDiscoveryTab === 'related' && (
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                    Google Related Searches for <code>"{seedBase}"</code>:
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {[
                      `how much does ${seedBase} cost in ${location}`,
                      `top rated ${seedBase} technicians`,
                      `${seedBase} warranty parts`
                    ].map((rel, i) => (
                      <div key={`rel-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(15, 23, 42, 0.6)', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', color: '#fff' }}>
                        <span>🔗 {rel}</span>
                        <Button
                          variant="secondary"
                          size="sm"
                          style={{ padding: '2px 6px', fontSize: '10px' }}
                          onClick={() => {
                            handleAddCandidate(rel, 'Related', 140 + i * 30, 'Informational');
                            const next = [...servicePages];
                            next[activePageIndex].methodsPerformed.relatedSearches = true;
                            updateServicePages(next);
                          }}
                        >
                          + Add Candidate
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

            {/* MANUAL KEYWORD ADD FORM */}
            <div style={{ display: 'flex', gap: '6px' }}>
              <input
                type="text"
                className="input-field"
                style={{ flex: 1, padding: '4px 8px', fontSize: '11px' }}
                placeholder="Or type custom keyword term..."
                value={customKwInput}
                onChange={(e) => setCustomKwInput(e.target.value)}
              />
              <input
                type="number"
                className="input-field"
                style={{ width: '65px', padding: '4px 6px', fontSize: '11px' }}
                placeholder="Vol"
                value={customVolInput}
                onChange={(e) => setCustomVolInput(e.target.value)}
              />
              <Button
                variant="primary"
                size="sm"
                style={{ padding: '4px 10px', fontSize: '11px' }}
                onClick={() => {
                  if (customKwInput) {
                    const parsedVol = isNaN(parseInt(customVolInput)) ? 0 : parseInt(customVolInput);
                    handleAddCandidate(customKwInput, 'Manual', parsedVol, 'Commercial');
                    setCustomKwInput('');
                  }
                }}
              >
                + Add
              </Button>
            </div>

            {/* DISCOVERED CANDIDATE POOL TABLE */}
            <div style={{ border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
              <div style={{ padding: '6px 10px', background: '#020617', borderBottom: '1px solid var(--border-color)', fontSize: '11px', fontWeight: 800, color: 'var(--accent-blue)', display: 'flex', justifyContent: 'space-between' }}>
                <span>CANDIDATE POOL ({(activePage.candidatePool || []).length} TERMS)</span>
                <span>VOL & INTENT</span>
              </div>

              <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                {(activePage.candidatePool || []).length === 0 ? (
                  <div style={{ padding: '16px', textTransform: 'none', textAlign: 'center', fontSize: '11px', color: 'var(--text-muted)' }}>
                    No candidates added yet. Use the generators above to populate terms for this service page!
                  </div>
                ) : (
                  (activePage.candidatePool || []).map((cand) => {
                    const isPrimary = (activePage.primaryKeyword || '').toLowerCase() === cand.keyword.toLowerCase();
                    const isSecondary = (activePage.secondaryKeyword || '').toLowerCase() === cand.keyword.toLowerCase();
                    const isTertiary = (activePage.tertiaryKeyword || '').toLowerCase() === cand.keyword.toLowerCase();
                    const isSupporting = (activePage.supportingKeywords || []).some(s => s.toLowerCase() === cand.keyword.toLowerCase());

                    return (
                      <div
                        key={cand.id}
                        style={{
                          padding: '8px 10px',
                          borderBottom: '1px solid rgba(255,255,255,0.05)',
                          background: isPrimary ? 'rgba(234, 179, 8, 0.12)' : isSecondary ? 'rgba(52, 211, 153, 0.12)' : isTertiary ? 'rgba(56, 189, 248, 0.12)' : 'rgba(15, 23, 42, 0.4)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', fontWeight: 800, color: '#ffffff' }}>{cand.keyword}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '10px', color: 'var(--accent-green)', fontWeight: 700 }}>{cand.searchVolume} vol</span>
                            <Badge variant="tool">{cand.intent}</Badge>
                            <button
                              onClick={() => handleRemoveCandidate(cand.id)}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0 2px' }}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>

                        {/* 1-CLICK ASSIGNMENT BUTTONS */}
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleSetRole('primary', cand.keyword, cand.searchVolume)}
                            style={{ background: isPrimary ? '#facc15' : 'rgba(234, 179, 8, 0.2)', color: isPrimary ? '#000' : '#facc15', border: '1px solid #facc15', borderRadius: '3px', padding: '2px 6px', fontSize: '10px', fontWeight: 800, cursor: 'pointer' }}
                          >
                            {isPrimary ? '✓ 🥇 Primary' : '+ 🥇 Set Primary'}
                          </button>
                          <button
                            onClick={() => handleSetRole('secondary', cand.keyword, cand.searchVolume)}
                            style={{ background: isSecondary ? '#34d399' : 'rgba(52, 211, 153, 0.2)', color: isSecondary ? '#000' : '#34d399', border: '1px solid #34d399', borderRadius: '3px', padding: '2px 6px', fontSize: '10px', fontWeight: 800, cursor: 'pointer' }}
                          >
                            {isSecondary ? '✓ 🥈 Secondary' : '+ 🥈 Set Secondary'}
                          </button>
                          <button
                            onClick={() => handleSetRole('tertiary', cand.keyword, cand.searchVolume)}
                            style={{ background: isTertiary ? '#38bdf8' : 'rgba(56, 189, 248, 0.2)', color: isTertiary ? '#000' : '#38bdf8', border: '1px solid #38bdf8', borderRadius: '3px', padding: '2px 6px', fontSize: '10px', fontWeight: 800, cursor: 'pointer' }}
                          >
                            {isTertiary ? '✓ 🥉 Tertiary' : '+ 🥉 Set Tertiary'}
                          </button>
                          <button
                            onClick={() => handleSetRole('supporting', cand.keyword)}
                            style={{ background: isSupporting ? '#c084fc' : 'rgba(192, 132, 252, 0.2)', color: isSupporting ? '#000' : '#c084fc', border: '1px solid #c084fc', borderRadius: '3px', padding: '2px 6px', fontSize: '10px', fontWeight: 800, cursor: 'pointer' }}
                          >
                            {isSupporting ? '✓ 📦 Supporting' : '+ 📦 Supporting'}
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* PANEL B: TARGET HIERARCHY & LIVE SERP OVERLAP WORKSTATION (STEP 10 MERGED) */}
          <div style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Layers size={16} color="#facc15" />
                  <span>STEP 10: HIERARCHY & SERP FINALIZER</span>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  Confirm 🥇 Primary, 🥈 Secondary, and 🥉 Tertiary targets and check SERP overlaps.
                </div>
              </div>
              <Badge variant={activePage.primaryKeyword && activePage.secondaryKeyword ? 'complete' : 'tool'}>
                {activePage.primaryKeyword && activePage.secondaryKeyword ? '✓ Finalized' : 'In Progress'}
              </Badge>
            </div>

            {/* 🤖 AI KEYWORD ARCHITECTURE CLASSIFIER (FOR 0-VOLUME / NICHE SERVICES) */}
            <div style={{ background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95))', border: '1px solid rgba(56, 189, 248, 0.4)', borderRadius: 'var(--radius-sm)', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Bot size={18} color="#38bdf8" />
                  <span style={{ fontSize: '12px', fontWeight: 800, color: '#ffffff' }}>AI KEYWORD CLASSIFIER & PROMPT GENERATOR</span>
                </div>
                <Badge variant="tool">0-Vol / Niche Assistant</Badge>
              </div>

              <div style={{ fontSize: '11px', color: '#cbd5e1', lineHeight: '1.4' }}>
                When terms have 0 search volume, use our custom AI prompt to analyze Intent, SERP Broadness & Entity Focus to auto-classify targets.
              </div>

              {/* ACTION BUTTONS */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Button
                  variant="primary"
                  size="sm"
                  style={{ flex: 1, minWidth: '170px', fontSize: '11px', background: aiCopied ? '#10b981' : undefined }}
                  onClick={() => {
                    const promptText = generateAiClassifierPrompt();
                    navigator.clipboard.writeText(promptText);
                    setAiCopied(true);
                    setTimeout(() => setAiCopied(false), 3000);
                  }}
                >
                  {aiCopied ? <Check size={13} style={{ marginRight: '4px' }} /> : <Copy size={13} style={{ marginRight: '4px' }} />}
                  {aiCopied ? '✓ Prompt Copied!' : '📋 Copy AI Prompt (ChatGPT/Gemini)'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  style={{ fontSize: '11px', padding: '4px 8px' }}
                  onClick={() => setShowAiPrompt(!showAiPrompt)}
                >
                  {showAiPrompt ? '🙈 Hide Preview' : '👁️ Preview Prompt'}
                </Button>
              </div>

              {/* PROMPT PREVIEW BOX */}
              {showAiPrompt && (
                <div style={{ background: '#020617', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', padding: '8px', fontSize: '10px', fontFamily: 'monospace', color: '#94a3b8', maxHeight: '160px', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                  {generateAiClassifierPrompt()}
                </div>
              )}

              {/* PASTE & AUTO-FILL BOX */}
              <div style={{ marginTop: '2px', borderTop: '1px dashed rgba(255,255,255,0.1)', paddingTop: '8px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#38bdf8', display: 'block', marginBottom: '4px' }}>
                  📥 Paste LLM Output JSON to Auto-Fill:
                </label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <textarea
                    className="input-field"
                    rows={2}
                    style={{ flex: 1, padding: '6px 8px', fontSize: '11px', fontFamily: 'monospace', color: '#ffffff', resize: 'vertical' }}
                    placeholder='Paste raw JSON output from ChatGPT or Gemini...'
                    value={aiJsonInput}
                    onChange={(e) => setAiJsonInput(e.target.value)}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    style={{ padding: '0 12px', fontSize: '11px', background: 'var(--accent-green)', color: '#000', fontWeight: 800 }}
                    onClick={() => handleApplyAiJson(aiJsonInput)}
                  >
                    <Zap size={14} style={{ marginRight: '4px' }} /> Auto-Fill
                  </Button>
                </div>

                {aiSuccessMsg && (
                  <div style={{ marginTop: '6px', fontSize: '11px', fontWeight: 700, color: '#34d399', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Check size={14} /> {aiSuccessMsg}
                  </div>
                )}
              </div>
            </div>

            {/* 🥇 PRIMARY KEYWORD SLOTS (2 TERMS MINIMUM) */}
            <div style={{ background: 'rgba(234, 179, 8, 0.1)', border: '1px solid #facc15', borderRadius: 'var(--radius-sm)', padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: 800, color: '#facc15' }}>🥇 PRIMARY TARGET KEYWORDS (2 REQUIRED)</span>
                {activePage.primaryKeyword && (
                  <a
                    href={`https://www.google.com/search?q=${encodeURIComponent(activePage.primaryKeyword)}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: '10px', color: '#38bdf8', textDecoration: 'none', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    Search Candidate on Google ↗
                  </a>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div>
                  <label style={{ fontSize: '10px', color: '#facc15', fontWeight: 700 }}>Primary #1 (Main Target):</label>
                  <input
                    type="text"
                    className="input-field"
                    style={{ padding: '6px 8px', fontSize: '12px', fontWeight: 800, width: '100%', color: '#ffffff' }}
                    placeholder="Primary #1 (e.g. commercial engine repair trinidad)..."
                    value={activePage.primaryKeyword}
                    onChange={(e) => {
                      const next = [...servicePages];
                      next[activePageIndex].primaryKeyword = e.target.value;
                      updateServicePages(next);
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '10px', color: '#facc15', fontWeight: 700 }}>Primary #2 (Main Variation):</label>
                  <input
                    type="text"
                    className="input-field"
                    style={{ padding: '6px 8px', fontSize: '12px', fontWeight: 800, width: '100%', color: '#ffffff' }}
                    placeholder="Primary #2 (e.g. truck engine repair in trinidad)..."
                    value={activePage.primaryKeyword2 || ''}
                    onChange={(e) => {
                      const next = [...servicePages];
                      next[activePageIndex].primaryKeyword2 = e.target.value;
                      updateServicePages(next);
                    }}
                  />
                </div>
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '6px', display: 'flex', justifyContent: 'space-between' }}>
                <span>Search Volume: <strong>{activePage.primaryVolume || 0} / mo</strong></span>
                <span>Page Role: Main H1 Heading & Hook Target</span>
              </div>
            </div>

            {/* 🥈 SECONDARY KEYWORD SLOTS (2 TERMS MINIMUM) */}
            <div style={{ background: 'rgba(52, 211, 153, 0.1)', border: '1px solid #34d399', borderRadius: 'var(--radius-sm)', padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: 800, color: '#34d399' }}>🥈 SECONDARY TARGET KEYWORDS (2 REQUIRED)</span>
                {activePage.secondaryKeyword && (
                  <a
                    href={`https://www.google.com/search?q=${encodeURIComponent(activePage.secondaryKeyword)}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: '10px', color: '#38bdf8', textDecoration: 'none', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    Compare SERP vs Primary ↗
                  </a>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div>
                  <label style={{ fontSize: '10px', color: '#34d399', fontWeight: 700 }}>Secondary #1 (Main Target):</label>
                  <input
                    type="text"
                    className="input-field"
                    style={{ padding: '6px 8px', fontSize: '12px', fontWeight: 800, width: '100%', color: '#ffffff' }}
                    placeholder="Secondary #1 (e.g. diesel engine overhaul)..."
                    value={activePage.secondaryKeyword}
                    onChange={(e) => {
                      const next = [...servicePages];
                      next[activePageIndex].secondaryKeyword = e.target.value;
                      updateServicePages(next);
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '10px', color: '#34d399', fontWeight: 700 }}>Secondary #2 (Main Variation):</label>
                  <input
                    type="text"
                    className="input-field"
                    style={{ padding: '6px 8px', fontSize: '12px', fontWeight: 800, width: '100%', color: '#ffffff' }}
                    placeholder="Secondary #2 (e.g. commercial diesel engine overhaul)..."
                    value={activePage.secondaryKeyword2 || ''}
                    onChange={(e) => {
                      const next = [...servicePages];
                      next[activePageIndex].secondaryKeyword2 = e.target.value;
                      updateServicePages(next);
                    }}
                  />
                </div>
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '6px', display: 'flex', justifyContent: 'space-between' }}>
                <span>Search Volume: <strong>{activePage.secondaryVolume || 0} / mo</strong></span>
                <span>Page Role: H2 Sub-Heading Target</span>
              </div>
            </div>

            {/* 🥉 TERTIARY KEYWORD SLOTS (2 TERMS MINIMUM) */}
            <div style={{ background: 'rgba(56, 189, 248, 0.1)', border: '1px solid #38bdf8', borderRadius: 'var(--radius-sm)', padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: 800, color: '#38bdf8' }}>🥉 TERTIARY TARGET KEYWORDS (2 REQUIRED)</span>
                {activePage.tertiaryKeyword && (
                  <a
                    href={`https://www.google.com/search?q=${encodeURIComponent(activePage.tertiaryKeyword)}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: '10px', color: '#38bdf8', textDecoration: 'none', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    Compare SERP vs Secondary ↗
                  </a>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div>
                  <label style={{ fontSize: '10px', color: '#38bdf8', fontWeight: 700 }}>Tertiary #1 (Main Target):</label>
                  <input
                    type="text"
                    className="input-field"
                    style={{ padding: '6px 8px', fontSize: '12px', fontWeight: 800, width: '100%', color: '#ffffff' }}
                    placeholder="Tertiary #1 (e.g. truck cylinder head repair)..."
                    value={activePage.tertiaryKeyword}
                    onChange={(e) => {
                      const next = [...servicePages];
                      next[activePageIndex].tertiaryKeyword = e.target.value;
                      updateServicePages(next);
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '10px', color: '#38bdf8', fontWeight: 700 }}>Tertiary #2 (Main Variation):</label>
                  <input
                    type="text"
                    className="input-field"
                    style={{ padding: '6px 8px', fontSize: '12px', fontWeight: 800, width: '100%', color: '#ffffff' }}
                    placeholder="Tertiary #2 (e.g. engine head rebuilding)..."
                    value={activePage.tertiaryKeyword2 || ''}
                    onChange={(e) => {
                      const next = [...servicePages];
                      next[activePageIndex].tertiaryKeyword2 = e.target.value;
                      updateServicePages(next);
                    }}
                  />
                </div>
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '6px', display: 'flex', justifyContent: 'space-between' }}>
                <span>Search Volume: <strong>{activePage.tertiaryVolume || 0} / mo</strong></span>
                <span>Page Role: H2/H3 Section Target</span>
              </div>
            </div>

            {/* 📦 SUPPORTING KEYWORDS POOL */}
            <div style={{ background: '#020617', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', padding: '10px' }}>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#c084fc', marginBottom: '6px' }}>
                📦 SUPPORTING KEYWORDS POOL ({(activePage.supportingKeywords || []).length} TERMS)
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {(activePage.supportingKeywords || []).length === 0 ? (
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>No supporting keywords added yet.</span>
                ) : (
                  (activePage.supportingKeywords || []).map((sk, i) => (
                    <KeywordCapsule
                      key={`sk-${i}`}
                      keyword={sk}
                      tier="supporting"
                      onRemove={() => {
                        const next = [...servicePages];
                        next[activePageIndex].supportingKeywords = (next[activePageIndex].supportingKeywords || []).filter(s => s !== sk);
                        updateServicePages(next);
                      }}
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          {/* PANEL C: CANNIBALIZATION GUARD & METHODOLOGY CHECKLIST */}
          <div style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px' }}>
              <div style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ShieldCheck size={16} color="var(--accent-green)" />
                <span>AUDIT & METHODOLOGY CHECKLIST</span>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                Real-time cannibalization check & research methodology tracker.
              </div>
            </div>

            {/* LIVE CANNIBALIZATION AUDIT ALERTS */}
            <div>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', marginBottom: '6px', textTransform: 'uppercase' }}>
                🛡️ Live Keyword Cannibalization Guard
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {[
                  { role: 'Primary', val: activePage.primaryKeyword },
                  { role: 'Secondary', val: activePage.secondaryKeyword },
                  { role: 'Tertiary', val: activePage.tertiaryKeyword }
                ].map(({ role, val }) => {
                  const check = checkCannibalization(val);
                  return (
                    <div
                      key={`guard-${role}`}
                      style={{
                        background: check.isConflict ? 'rgba(239, 68, 68, 0.15)' : val ? 'rgba(16, 185, 129, 0.15)' : 'rgba(15, 23, 42, 0.4)',
                        border: check.isConflict ? '1px solid #ef4444' : val ? '1px solid #10b981' : '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 'var(--radius-sm)',
                        padding: '8px 10px',
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <div>
                        <strong>{role}:</strong> {val || 'Not Set'}
                      </div>
                      {check.isConflict ? (
                        <span style={{ color: '#ef4444', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <AlertTriangle size={12} /> Conflict with {check.conflictSource}
                        </span>
                      ) : val ? (
                        <span style={{ color: '#34d399', fontWeight: 800 }}>✓ Unique & Clean Target</span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>Pending Selection</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 10-STEP METHODOLOGY CHECKLIST TRACKER */}
            <div style={{ background: '#020617', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', padding: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', marginBottom: '8px', textTransform: 'uppercase' }}>
                📋 Merged Research Methodology Checklist
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '11px', color: '#e2e8f0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={activePage.methodsPerformed.autocomplete}
                    onChange={(e) => {
                      const next = [...servicePages];
                      next[activePageIndex].methodsPerformed.autocomplete = e.target.checked;
                      updateServicePages(next);
                    }}
                  />
                  <span>🔤 Autocomplete Verified (Step 3)</span>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={activePage.methodsPerformed.competitorScraping}
                    onChange={(e) => {
                      const next = [...servicePages];
                      next[activePageIndex].methodsPerformed.competitorScraping = e.target.checked;
                      updateServicePages(next);
                    }}
                  />
                  <span>🔍 Competitor Titles Scraped (Step 4 & 5)</span>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={activePage.methodsPerformed.relatedSearches}
                    onChange={(e) => {
                      const next = [...servicePages];
                      next[activePageIndex].methodsPerformed.relatedSearches = e.target.checked;
                      updateServicePages(next);
                    }}
                  />
                  <span>🔗 Google Related Searches Evaluated (Step 6)</span>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={activePage.methodsPerformed.toolsImport}
                    onChange={(e) => {
                      const next = [...servicePages];
                      next[activePageIndex].methodsPerformed.toolsImport = e.target.checked;
                      updateServicePages(next);
                    }}
                  />
                  <span>📊 Search Volume & Intent Verified (Steps 7–9)</span>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={activePage.methodsPerformed.serpCheck}
                    onChange={(e) => {
                      const next = [...servicePages];
                      next[activePageIndex].methodsPerformed.serpCheck = e.target.checked;
                      updateServicePages(next);
                    }}
                  />
                  <span>🎯 SERP Overlap & Cannibalization Audited (Step 10)</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 4. MASTER SITE ARCHITECTURE SUMMARY TABLE */}
      <div style={{ border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: 'rgba(15, 23, 42, 0.9)' }}>
        <div style={{ padding: '12px 16px', background: '#020617', borderBottom: '1px solid var(--border-color)', fontSize: '13px', fontWeight: 800, color: 'var(--accent-blue)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>📊 MASTER SITE ARCHITECTURE & KEYWORD MATRIX OVERVIEW</span>
          <Badge variant="complete">{1 + servicePages.length} TOTAL OPTIMIZED PAGES</Badge>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#090d16', color: '#94a3b8', fontSize: '11px', textTransform: 'uppercase' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>Page Name</th>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>URL Path</th>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>🥇 Primary Target</th>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>🥈 Secondary Target</th>
              <th style={{ padding: '10px 14px', textAlign: 'left' }}>🥉 Tertiary Target</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', width: '120px' }}>Audit Status</th>
            </tr>
          </thead>
          <tbody>
            {/* Homepage row */}
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(234, 179, 8, 0.08)' }}>
              <td style={{ padding: '10px 14px', fontSize: '12px', fontWeight: 800, color: '#facc15' }}>Homepage</td>
              <td style={{ padding: '10px 14px', fontSize: '11px', color: '#38bdf8', fontFamily: 'monospace' }}>/</td>
              <td style={{ padding: '10px 14px', fontSize: '12px', color: '#ffffff', fontWeight: 700 }}>{masterPrimary}</td>
              <td style={{ padding: '10px 14px', fontSize: '12px', color: '#cbd5e1' }}>{masterSecondary}</td>
              <td style={{ padding: '10px 14px', fontSize: '12px', color: '#94a3b8' }}>{masterTertiary}</td>
              <td style={{ padding: '10px 14px' }}><Badge variant="complete">✓ Step 10 Done</Badge></td>
            </tr>

            {/* Service page rows */}
            {servicePages.map((sp, idx) => {
              const isComplete = sp.primaryKeyword && sp.secondaryKeyword;
              return (
                <tr key={`matrix-sp-${sp.id}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: idx % 2 === 0 ? 'rgba(15, 23, 42, 0.6)' : 'rgba(15, 23, 42, 0.3)' }}>
                  <td style={{ padding: '10px 14px', fontSize: '12px', fontWeight: 800, color: '#ffffff' }}>{sp.pageName || `Service #${idx + 1}`}</td>
                  <td style={{ padding: '10px 14px', fontSize: '11px', color: '#38bdf8', fontFamily: 'monospace' }}>{sp.targetUrl}</td>
                  <td style={{ padding: '10px 14px', fontSize: '12px', color: '#ffffff', fontWeight: 700 }}>{sp.primaryKeyword || '—'}</td>
                  <td style={{ padding: '10px 14px', fontSize: '12px', color: '#cbd5e1' }}>{sp.secondaryKeyword || '—'}</td>
                  <td style={{ padding: '10px 14px', fontSize: '12px', color: '#94a3b8' }}>{sp.tertiaryKeyword || '—'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <Badge variant={isComplete ? 'complete' : 'tool'}>
                      {isComplete ? '✓ Finalized' : 'In Progress'}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
